import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Container,
  useTheme,
  alpha
} from '@mui/material';
import {
  Block as BlockIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { supabase } from '../supabaseClient';

export default function CompanyDisabled({ 
  companyName, 
  companyId, 
  isOwner, 
  onReactivationSuccess 
}) {
  const theme = useTheme();
  const [reactivating, setReactivating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const handleReactivateCompany = async () => {
    setReactivating(true);
    setError('');
    setSuccess('');

    try {
      const { error } = await supabase
        .from('companies')
        .update({ 
          is_active: true,
          deactivated_at: null,
          deactivated_by: null
        })
        .eq('id', companyId);

      if (error) throw error;

      setSuccess('Company reactivated successfully! Refreshing...');
      
      setTimeout(() => {
        // Call the onReactivationSuccess callback instead of reloading
        if (onReactivationSuccess) {
          onReactivationSuccess();
        }
      }, 2000);
    } catch (err) {
      console.error('Error reactivating company:', err);
      setError('Failed to reactivate company: ' + err.message);
    } finally {
      setReactivating(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      px: 3,
      py: 4,
      position: 'relative'
    }}>
      {/* GlowWorks logo */}
      <Box sx={{ position: 'absolute', top: 32, left: 32, zIndex: 10 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 500,
            color: '#000',
            fontSize: '1.8rem',
            letterSpacing: '0.5px',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            cursor: 'default',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'scale(1.02)',
              textShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
            }
          }}
        >
          GlowWorks
        </Typography>
      </Box>

      {/* Sign Out Button */}
      <Box sx={{ position: 'absolute', bottom: 32, left: 32, zIndex: 10 }}>
        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={handleSignOut}
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.5,
            fontSize: '0.9rem',
            fontWeight: 500,
            minWidth: 180,
            color: 'text.secondary',
            borderColor: alpha(theme.palette.text.secondary, 0.3),
            backgroundColor: alpha('#fff', 0.8),
            backdropFilter: 'blur(10px)',
            '&:hover': {
              borderColor: 'error.main',
              color: 'error.main',
              backgroundColor: alpha(theme.palette.error.main, 0.05),
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
            }
          }}
        >
          Sign Out
        </Button>
      </Box>
      <Card sx={{ 
        width: '100%',
        maxWidth: 600,
        borderRadius: 4,
        border: `1px solid ${theme.palette.error.main}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.05)}, ${alpha(theme.palette.error.main, 0.02)})`,
        boxShadow: `0 20px 60px ${alpha(theme.palette.error.main, 0.15)}`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 24px 72px ${alpha(theme.palette.error.main, 0.25)}`,
          border: `1px solid ${theme.palette.error.dark}`,
        }
      }}>
        <CardContent sx={{ p: 6, pt: 8 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{
              display: 'inline-flex',
              p: 3,
              borderRadius: '50%',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
              mb: 3
            }}>
              <BlockIcon sx={{ 
                fontSize: 48, 
                color: 'error.main' 
              }} />
            </Box>

            <Typography variant="h3" sx={{ 
              fontWeight: 500,
              color: 'error.main',
              fontSize: '2rem',
              mb: 2
            }}>
              Company Deactivated
            </Typography>

            <Typography variant="h5" sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 1
            }}>
              {companyName}
            </Typography>

            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.6,
              maxWidth: 450,
              mx: 'auto'
            }}>
              This company has been temporarily deactivated by an administrator. 
              {isOwner 
                ? ' As the company owner, you can reactivate it below.'
                : ' Please contact your company administrator for assistance.'
              }
            </Typography>
          </Box>

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              {success}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            alignItems: 'center'
          }}>
            {isOwner && (
              <Button
                variant="contained"
                size="large"
                startIcon={reactivating ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleReactivateCompany}
                disabled={reactivating}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                  backgroundColor: 'success.main',
                  minWidth: 200,
                  '&:hover': {
                    backgroundColor: 'success.dark',
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                  }
                }}
              >
                {reactivating ? 'Reactivating...' : 'Reactivate Company'}
              </Button>
            )}

            <Button
              variant="outlined"
              size="large"
              startIcon={<RefreshIcon />}
              onClick={() => window.location.reload()}
              disabled={reactivating}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '0.95rem',
                fontWeight: 500,
                color: 'text.secondary',
                borderColor: alpha(theme.palette.text.secondary, 0.3),
                '&:hover': {
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  backgroundColor: alpha(theme.palette.primary.main, 0.05)
                }
              }}
            >
              Refresh Page
            </Button>
          </Box>

          {/* Additional Info */}
          <Box sx={{ 
            mt: 4, 
            pt: 3,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            textAlign: 'center'
          }}>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontSize: '0.85rem'
            }}>
              If you need assistance, please contact support or your system administrator.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}